/**
 * Import a guideline PDF — `POST /admin/data/guidelines/import` (multipart:
 * file, title, product?, slug?, notify?, codify?).
 *
 * The backend does a deterministic pdf→markdown conversion and then (when
 * `codify` is on, the default) runs the codify agent to produce structured.json.
 * That agent pass is slow — the API module gives this call a 10-minute timeout
 * and the dialog blocks with an explicit "this takes a few minutes" state rather
 * than looking hung.
 *
 * The backend rejects anything that isn't a real PDF with a 422 ("Not a PDF"),
 * so we check the extension client-side to fail fast.
 */
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { adminGuidelinesApi } from "../../../api/adminDataApi";

const PRODUCTS = ["DSCR", "RTL", "GUC", "HVRTL"];

export default function ImportGuidelinePdfDialog({ open, onClose, onImported }) {
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [product, setProduct] = useState("");
  const [slug, setSlug] = useState("");
  const [codify, setCodify] = useState(true);
  const [notify, setNotify] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) return;
    setFile(null);
    setTitle("");
    setProduct("");
    setSlug("");
    setCodify(true);
    setNotify(false);
    setBusy(false);
  }, [open]);

  const pick = useCallback((e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFile(f);
    setTitle((prev) => prev || f.name.replace(/\.pdf$/i, ""));
  }, []);

  const submit = useCallback(() => {
    if (!file || !title.trim()) return;
    setBusy(true);
    adminGuidelinesApi
      .import(file, {
        title: title.trim(),
        product: product || undefined,
        slug: slug.trim() || undefined,
        codify,
        notify,
      })
      .then((out) => {
        enqueueSnackbar(`Imported as v${out?.version}`, { variant: "success" });
        onImported?.(out);
      })
      .catch((err) =>
        enqueueSnackbar(
          err?.response?.data?.detail || "Guideline import failed",
          { variant: "error" },
        ),
      )
      .finally(() => setBusy(false));
  }, [file, title, product, slug, codify, notify, enqueueSnackbar, onImported]);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        Import guideline PDF
      </DialogTitle>
      <DialogContent dividers>
        <Flx column g={2} sx={{ pt: 1 }}>
          <Flx ac g={1.5} wrap>
            <Button variant="outlined" component="label" disabled={busy}>
              {file ? "Change PDF" : "Choose PDF"}
              <input type="file" accept="application/pdf,.pdf" hidden onChange={pick} />
            </Button>
            <Txt sx={{ fontSize: 12.5, color: file ? "#0f172a" : "#94a3b8" }}>
              {file ? file.name : "No file selected"}
            </Txt>
          </Flx>

          <TextField
            size="small"
            label="Title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy}
            fullWidth
          />
          <TextField
            select
            size="small"
            label="Product"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            disabled={busy}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          >
            <option value="" />
            {PRODUCTS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Slug (optional — derived from the title when blank)"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            disabled={busy}
            helperText="Importing against an existing slug adds a new version to that guideline."
            fullWidth
          />

          <Flx column>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={codify}
                  onChange={(e) => setCodify(e.target.checked)}
                  disabled={busy}
                />
              }
              label={
                <Txt sx={{ fontSize: 12.5 }}>
                  Run the codify agent (produces the structured rules index)
                </Txt>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                  disabled={busy}
                />
              }
              label={
                <Txt sx={{ fontSize: 12.5 }}>
                  Notify admin / underwriting / processing
                </Txt>
              }
            />
          </Flx>

          {busy ? (
            <Flx column g={1}>
              <Txt sx={{ fontSize: 12.5, color: "#64748b" }}>
                Converting the PDF
                {codify ? " and running the codify agent" : ""} — this can take a
                few minutes. Leave this dialog open.
              </Txt>
              <LinearProgress />
            </Flx>
          ) : null}
        </Flx>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !file || !title.trim()}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ImportGuidelinePdfDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onImported: PropTypes.func,
};
